/**
 * FIDE C.04.6 team bracket pairing — the case bbpPairings cannot be given.
 *
 * WHY THIS EXISTS AT ALL, given that team pairing here is otherwise done by bbpPairings
 * (js/services/team-pairing-trf.js). That route works by writing each TEAM as one
 * "player" in a TRF, and a TRF records a round as a single character: for a played game
 * the engine accepts only win, draw or loss. Match points fit exactly — a match is won,
 * drawn or lost. GAME points do not: over four boards a team scores one of nine values
 * (0, ½, 1 … 4), and three codes cannot carry nine outcomes. Nor can the total simply be
 * declared, because the engine recomputes the score from the cells and rejects the whole
 * file when they disagree (vendor/bbpPairings/src/fileformats/trf.cpp:939).
 *
 * A competition that ranks teams by board points — which C.04.6 explicitly allows, and
 * which the TRF type code FIDE_TEAM_TYPEA_GP_MP names — therefore cannot be paired
 * through that engine at all. This module pairs it directly.
 *
 * SCOPE, deliberately narrow. bbpPairings remains the pairing path whenever it CAN be
 * used; it is FIDE-endorsed, and it is exercised against upstream's own fixtures. This is
 * the fallback for what it cannot express, and it implements C.04.6's own criteria, which
 * are markedly simpler than the Dutch system's:
 *
 *   Absolute   [C1] no two teams meet twice
 *              [C2] no second bye
 *              [C3] the teams left over must still be pairable
 *   Quality    [C4] fewest upfloaters            [C5] smallest score differences
 *              [C7] fewest repeat upfloaters     [C8] fewest unfulfilled colour preferences
 *              [C9] (Type B) fewest unfulfilled strong preferences
 *              [C10] fewest repeat downfloated opponents
 *
 * [C6] — that the pairing must leave the FOLLOWING scoregroup able to satisfy [C4] — is a
 * tie-break applied below [C5], which is where art. 3.7 puts it: a bracket is never made
 * worse in order to tidy up the one beneath it. Feasibility itself ([C3]) is absolute and
 * checked on every candidate.
 *
 * Colours are NOT decided here. C.04.6 art. 3.2 pairs first and allocates colours as a
 * separate final step, which is TeamEngine.assignRoundColours.
 */

const TeamPairer = {

    /**
     * Can these two be paired? [C1]: never twice, whatever the result was — a match
     * awarded by forfeit was still a pairing.
     */
    canPair(aId, bId, stats) {
        return aId !== bId && !stats[aId].opponents.has(bId);
    },

    /**
     * Does a perfect pairing of `pool` exist using only legal pairs?
     *
     * The feasibility test behind [C3] and [C6]: a bracket may not be paired in a way
     * that strands the teams below it. Always matches the FIRST unpaired team, which
     * generates each distinct pairing once and terminates immediately on the near-complete
     * graphs a team event produces — two teams have usually not met.
     *
     * `budget` bounds a pathological field. Running out is reported as "not pairable",
     * which is the conservative direction: the caller then adds an upfloater it might not
     * have needed, rather than committing a round it cannot complete.
     */
    isPairable(pool, stats, budget = { nodes: 200000 }) {
        const n = pool.length;
        if (n % 2 !== 0) return false;
        if (n === 0) return true;

        const used = new Array(n).fill(false);
        const seen = new Set();

        const search = (count) => {
            if (count === n) return true;
            if (budget.nodes-- <= 0) return false;

            let i = 0;
            while (i < n && used[i]) i++;

            // Two different orders that consumed the same teams face an identical
            // remaining problem, so a failed set never has to be explored twice.
            const key = used.reduce((acc, u, k) => u ? acc + ',' + k : acc, '');
            if (seen.has(key)) return false;
            seen.add(key);

            used[i] = true;
            for (let j = i + 1; j < n; j++) {
                if (used[j]) continue;
                if (!TeamPairer.canPair(pool[i], pool[j], stats)) continue;
                used[j] = true;
                if (search(count + 2)) return true;
                used[j] = false;
            }
            used[i] = false;
            return false;
        };

        return search(0);
    },

    /**
     * The team that sits out — C.04.6 art. 3.4.
     *
     * It must leave the rest pairable, and among those it is the team with the LOWEST
     * score, then the MOST matches played, then the LARGEST pairing number. [C2] keeps a
     * team that has already had one out of the running — unless every remaining team has,
     * in which case somebody still has to sit out and refusing to pair the round would be
     * worse than repeating a bye.
     *
     * @returns {string|null} null when no choice leaves a pairable remainder
     */
    selectBye(pool, stats, primaryOf) {
        const ordered = [...pool].sort((a, b) => {
            const sa = stats[a], sb = stats[b];
            return (primaryOf(sa) - primaryOf(sb))
                || (sb.matchesPlayed - sa.matchesPlayed)
                || (sb.teamNo - sa.teamNo);
        });

        const eligible = ordered.filter(id => !stats[id].hadBye);
        for (const list of [eligible, ordered]) {
            for (const id of list) {
                if (TeamPairer.isPairable(pool.filter(x => x !== id), stats)) return id;
            }
        }
        return null;
    },

    /**
     * Score one candidate pairing of a bracket, as a tuple compared left to right, every
     * entry minimised. The order is C.04.6 art. 3.7's own.
     *
     * [C4] (how many upfloaters) and the choice of WHICH teams float are settled by the
     * caller, which is why they are not here — within a fixed bracket they are constant.
     */
    scorePairing(pairs, ctx) {
        const { stats, primaryOf, lastFloat, colourPrefType, isFinalRound, suspendFloatHistory } = ctx;

        let scoreDiff = 0;      // [C5]
        let repeatUp = 0;       // [C7]
        let unfulfilled = 0;    // [C8]
        let unfulfilledStrong = 0; // [C9]
        let repeatDownOpp = 0;  // [C10]

        pairs.forEach(([a, b]) => {
            const sa = stats[a], sb = stats[b];
            const pa = primaryOf(sa), pb = primaryOf(sb);

            if (pa !== pb) {
                scoreDiff += Math.abs(pa - pb);
                const upId = pa < pb ? a : b;      // the team brought UP into this bracket
                const downId = upId === a ? b : a; // the resident it was brought up to meet
                // [C7] and [C10] are suspended for the last two rounds, where forcing
                // float variety would distort the pairings that decide the event.
                if (!suspendFloatHistory) {
                    if (lastFloat[upId] === 'up') repeatUp++;
                    if (lastFloat[downId] === 'down') repeatDownOpp++;
                }
            }

            const prefA = window.TeamEngine.colourPreference(sa, colourPrefType, isFinalRound);
            const prefB = window.TeamEngine.colourPreference(sb, colourPrefType, isFinalRound);
            // Both want the same colour, so one of them cannot have it.
            if (prefA.colour && prefB.colour && prefA.colour === prefB.colour) {
                unfulfilled++;
                if (prefA.strength === 'strong' && prefB.strength === 'strong') unfulfilledStrong++;
            }
        });

        const tuple = [scoreDiff, repeatUp, unfulfilled];
        if (colourPrefType === 'B') tuple.push(unfulfilledStrong);
        tuple.push(repeatDownOpp);
        return tuple;
    },

    /** Lexicographic comparison of two scorePairing tuples. */
    compareScores(a, b) {
        for (let i = 0; i < Math.max(a.length, b.length); i++) {
            const d = (a[i] || 0) - (b[i] || 0);
            if (d !== 0) return d;
        }
        return 0;
    },

    /**
     * The best legal pairing of one bracket, or null if it cannot be paired while leaving
     * `remainder` pairable.
     *
     * Candidate pairings are enumerated by always pairing the first unpaired team, which
     * produces each distinct pairing exactly once, and the lexicographically best is kept.
     * The node budget bounds a large bracket; the best found so far is still a legal
     * pairing, just not provably the optimal one.
     */
    bestBracketPairing(bracket, remainder, ctx) {
        const n = bracket.length;
        if (n === 0) return [];
        if (n % 2 !== 0) return null;

        let best = null;
        let bestScore = null;
        let nodes = 80000;

        const used = new Array(n).fill(false);
        const chosen = [];

        const search = (count) => {
            if (nodes-- <= 0) return;

            if (count === n) {
                // [C3] / [C6]: whatever this bracket leaves behind must still be pairable.
                if (remainder.length > 0 && !TeamPairer.isPairable(remainder, ctx.stats)) return;
                const score = TeamPairer.scorePairing(chosen, ctx);
                if (!bestScore || TeamPairer.compareScores(score, bestScore) < 0) {
                    bestScore = score;
                    best = chosen.map(p => [...p]);
                }
                return;
            }

            let i = 0;
            while (i < n && used[i]) i++;
            used[i] = true;
            for (let j = i + 1; j < n; j++) {
                if (used[j]) continue;
                if (!TeamPairer.canPair(bracket[i], bracket[j], ctx.stats)) continue;
                used[j] = true;
                chosen.push([bracket[i], bracket[j]]);
                search(count + 2);
                chosen.pop();
                used[j] = false;
                // Nothing can beat a tuple of zeroes, so stop once the natural pairing
                // has already satisfied everything — the common case by far.
                if (bestScore && bestScore.every(v => v === 0)) break;
            }
            used[i] = false;
        };

        search(0);
        return best;
    },

    /**
     * Choose which teams are brought up into a bracket, best candidates first.
     *
     * [C5] wants the score difference small and the upfloaters' own scores high, and
     * `below` is already in descending order — so the first `k` are the best possible
     * choice and later combinations are only tried when that one cannot be paired.
     * Capped, because the number of combinations is unbounded and k is almost always 1.
     */
    floaterCandidates(below, k, limit = 40) {
        if (k === 0) return [[]];
        const out = [];
        const combo = [];
        const walk = (start) => {
            if (out.length >= limit) return;
            if (combo.length === k) { out.push([...combo]); return; }
            for (let i = start; i < below.length; i++) {
                combo.push(below[i]);
                walk(i + 1);
                combo.pop();
                if (out.length >= limit) return;
            }
        };
        walk(0);
        return out;
    },

    /**
     * Can the next non-empty scoregroup in `remainder` pair itself, with nobody floating?
     *
     * This is [C6] — "the bracket pairing must comply with [C1], [C3] and [C4] in the
     * following non-empty scoregroup". It only ever breaks a tie on [C5], which sits above
     * it, so a bracket is never made worse in order to tidy up the one below.
     *
     * An odd next group cannot pair itself by definition and is not counted against the
     * candidate — somebody has to float into it whatever this bracket did.
     */
    nextGroupSelfPairable(remainder, ctx) {
        if (remainder.length === 0) return true;
        const top = ctx.primaryOf(ctx.stats[remainder[0]]);
        const group = remainder.filter(id => ctx.primaryOf(ctx.stats[id]) === top);
        if (group.length % 2 !== 0) return true;
        return TeamPairer.isPairable(group, ctx.stats);
    },

    /**
     * Pair one round of teams under C.04.6.
     *
     * @param {Array} teams   the field entered for this round
     * @param {Array} rounds  rounds played so far
     * @param {Object} opts   { primaryScore, matchPointSystem, pointSystem, boards,
     *                          colourPrefType, roundNo, totalRounds }
     * @returns {{ pairs: Array<[string,string]>, bye: string|null, stats: Object,
     *             brackets: Array, unpairable: boolean }}
     *          `pairs` is unordered within each pair — colours are art. 4.3's job.
     *          `brackets` records what each bracket cost — its score, how many teams lived
     *          there and how many were brought up — which is what makes [C4] checkable
     *          from outside, and what an arbiter needs to explain a pairing afterwards.
     */
    pairRound(teams, rounds, opts = {}) {
        const TeamEngine = window.TeamEngine;
        const primaryScore = opts.primaryScore || 'match';
        const roundNo = opts.roundNo || (rounds.length + 1);
        const totalRounds = parseInt(opts.totalRounds, 10) || 0;

        const stats = TeamEngine.calculateTeamStats(teams, rounds, opts);
        const primaryOf = (st) => primaryScore === 'game' ? st.gamePoints : st.matchPoints;
        const secondaryOf = (st) => primaryScore === 'game' ? st.matchPoints : st.gamePoints;

        // Last round's float per team, for [C7] and [C10].
        const history = TeamEngine.floatHistory(teams, rounds, opts);
        const lastFloat = {};
        Object.keys(history).forEach(id => {
            const h = history[id];
            lastFloat[id] = h.length ? h[h.length - 1] : null;
        });

        const ctx = {
            stats,
            primaryOf,
            lastFloat,
            colourPrefType: opts.colourPrefType || 'A',
            isFinalRound: totalRounds > 0 && roundNo === totalRounds,
            suspendFloatHistory: totalRounds > 0 && roundNo > totalRounds - 2
        };

        // Art. 4.4 order — higher primary, then higher secondary, then lower pairing
        // number. Everything below walks the field in this order.
        let pool = teams.map(t => t.id).sort((a, b) => {
            const sa = stats[a], sb = stats[b];
            return (primaryOf(sb) - primaryOf(sa))
                || (secondaryOf(sb) - secondaryOf(sa))
                || (sa.teamNo - sb.teamNo);
        });

        // Step 1 (art. 3.3): the bye comes out before any bracket is formed, so the
        // brackets below always see an even field.
        let bye = null;
        if (pool.length % 2 === 1) {
            bye = TeamPairer.selectBye(pool, stats, primaryOf);
            if (bye) pool = pool.filter(id => id !== bye);
        }

        // Steps 2-3 (art. 3.5-3.7): down the scoregroups, each bracket being the top one
        // plus as few upfloaters as will pair it.
        //
        // The criteria are applied in the order art. 3.7 states, and the order is the whole
        // point: [C4] (how many float) outranks [C5] (how far they float), which outranks
        // [C6] (leaving the next scoregroup able to satisfy [C4] itself). Following that
        // literally can produce more floats ACROSS THE WHOLE FIELD than a search that
        // optimised globally would — C.04.6 is bracket-sequential by construction, and
        // [C5] deliberately prefers a short float now over a tidier field later. The Dutch
        // system weighs this differently, so bbpPairings can legitimately return a pairing
        // with fewer total floats for the same position; see tests/team-pairer.test.js.
        const pairs = [];
        const brackets = [];

        while (pool.length > 0) {
            const topScore = primaryOf(stats[pool[0]]);
            const resident = pool.filter(id => primaryOf(stats[id]) === topScore);
            const below = pool.filter(id => primaryOf(stats[id]) !== topScore);

            let committed = null;

            // [C4]: as few upfloaters as possible. The bracket has to come out even, so k
            // shares the parity of the resident group and rises in twos.
            for (let k = resident.length % 2; k <= below.length && !committed; k += 2) {
                // Every candidate that can be paired at this k, with the [C5] cost of its
                // best pairing. Collected rather than taken first-come, so [C6] can break a
                // [C5] tie — which is the only thing [C6] is allowed to do, sitting below
                // [C5] in the order.
                const viable = [];
                for (const floaters of TeamPairer.floaterCandidates(below, k)) {
                    const bracket = [...resident, ...floaters];
                    const floaterSet = new Set(floaters);
                    const remainder = below.filter(id => !floaterSet.has(id));

                    const best = TeamPairer.bestBracketPairing(bracket, remainder, ctx);
                    if (!best) continue;

                    viable.push({
                        pairs: best,
                        consumed: new Set(bracket),
                        remainder,
                        c5: TeamPairer.scorePairing(best, ctx)[0]
                    });
                }
                if (viable.length === 0) continue;

                const bestC5 = Math.min(...viable.map(v => v.c5));
                const tied = viable.filter(v => v.c5 === bestC5);

                // [C6]: among equals, prefer a bracket that leaves the NEXT non-empty
                // scoregroup able to pair itself without floating anyone — that is what
                // "complies with C4 in the following scoregroup" asks for.
                committed = tied.find(v => TeamPairer.nextGroupSelfPairable(v.remainder, ctx)) || tied[0];
            }

            if (!committed) {
                // No legal pairing exists for this bracket however many teams are brought
                // up — every remaining team has met every other. Reported rather than
                // fudged: the caller asks the arbiter what to do.
                return { pairs, bye, stats, brackets, unpairable: true };
            }

            brackets.push({
                score: topScore,
                // The ids, not just the counts. `resident` is who was STILL IN THE POOL on
                // this score — teams on the same score may already have been brought up
                // into a higher bracket — so it is the only set against which [C4] can
                // honestly be checked, from a test or by an arbiter asked to explain a
                // pairing.
                residentIds: [...resident],
                upfloaterIds: [...committed.consumed].filter(id => !resident.includes(id)),
                residents: resident.length,
                upfloaters: committed.consumed.size - resident.length
            });
            committed.pairs.forEach(p => pairs.push(p));
            pool = pool.filter(id => !committed.consumed.has(id));
        }

        return { pairs, bye, stats, brackets, unpairable: false };
    }
};


if (typeof module !== 'undefined') {
    module.exports = { TeamPairer };
} else {
    window.TeamPairer = TeamPairer;
}
